import { useState, useMemo } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Stack,
  Avatar,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import GridViewIcon from "@mui/icons-material/GridView";
import PersonIcon from "@mui/icons-material/Person";
import CheckIcon from "@mui/icons-material/Check";
import GroupIcon from "@mui/icons-material/Group";

const BulkAssignModal = ({
  open,
  onClose,
  selectedLeads = [],
  filteredLeads = [],
  telecallers = [],
  onConfirm,
  onAssignComplete,
}) => {
  const theme = useTheme();
  const [assignmentLogic, setAssignmentLogic] = useState("specific"); // "equal" or "specific"
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState("all");
  const [internalNote, setInternalNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Get unique teams from telecallers
  const teams = useMemo(() => {
    const teamSet = new Set();
    telecallers.forEach((t) => {
      if (t.team) teamSet.add(t.team);
    });
    return ["all", ...Array.from(teamSet)];
  }, [telecallers]);

  // Filter telecallers based on search and team
  const filteredTelecallers = useMemo(() => {
    let result = telecallers;

    // Filter by team
    if (selectedTeam !== "all") {
      result = result.filter((t) => t.team === selectedTeam);
    }

    // Filter by search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (t) =>
          t.name?.toLowerCase().includes(q) ||
          t.email?.toLowerCase().includes(q) ||
          t.team?.toLowerCase().includes(q),
      );
    }

    return result;
  }, [telecallers, searchQuery, selectedTeam]);

  const getInitials = (name) => {
    if (!name) return "A";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const handleConfirm = async () => {
    if (assignmentLogic === "specific" && !selectedAgent) {
      return;
    }

    if (assignmentLogic === "equal" && filteredTelecallers.length === 0) {
      return;
    }

    setLoading(true);

    try {
      const leadsToAssign =
        selectedLeads.length > 0 ? selectedLeads : filteredLeads;

      if (assignmentLogic === "specific") {
        // Assign all leads to specific agent
        const assignData = {
          leadIds: leadsToAssign.map((l) => l._id || l.id),
          assignTo: selectedAgent.id,
          assignmentType: "specific",
          internalNote,
        };

        // Call the backend API (will be implemented)
        // For now, call the onConfirm callback
        if (onConfirm) {
          await onConfirm(assignData);
        }

        setSuccessMessage(
          `Successfully assigned ${leadsToAssign.length} leads to ${selectedAgent.name}`,
        );
      } else {
        // Equal distribution among all filtered agents
        const assignData = {
          leadIds: leadsToAssign.map((l) => l._id || l.id),
          assignToMultiple: filteredTelecallers.map((t) => t.id),
          assignmentType: "equal",
          team: selectedTeam === "all" ? null : selectedTeam,
          internalNote,
        };

        if (onConfirm) {
          await onConfirm(assignData);
        }

        setSuccessMessage(
          `Successfully distributed ${leadsToAssign.length} leads among ${filteredTelecallers.length} agents`,
        );
      }

      setShowSuccess(true);

      // Close modal after showing success and notify parent
      setTimeout(() => {
        handleClose();
        // Call the callback to refresh data
        if (onAssignComplete) {
          onAssignComplete();
        }
      }, 1500);
    } catch (error) {
      console.error("Error assigning leads:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAssignmentLogic("specific");
    setSearchQuery("");
    setSelectedAgent(null);
    setSelectedTeam("all");
    setInternalNote("");
    onClose();
  };

  const isConfirmDisabled =
    loading ||
    (assignmentLogic === "specific" && !selectedAgent) ||
    (assignmentLogic === "equal" && filteredTelecallers.length === 0);

  const displayCount =
    selectedLeads.length > 0 ? selectedLeads.length : filteredLeads.length;

  return (
    <>
      <Dialog
        open={open}
        onClose={loading ? undefined : handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            maxWidth: 500,
            m: 2,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            borderBottom: "1px solid #E2E8F0",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 700,
                color: "#0F172A",
                lineHeight: 1.3,
              }}
            >
              Bulk Assign Leads
            </Typography>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 500,
                color: theme.palette.primary.main,
                mt: 0.5,
              }}
            >
              {displayCount} lead{displayCount !== 1 ? "s" : ""} selected
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            disabled={loading}
            sx={{
              width: 32,
              height: 32,
              color: "#64748B",
              "&:hover": { bgcolor: "#F1F5F9" },
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ px: 3, py: 2.5, maxHeight: "70vh", overflowY: "auto" }}>
          {/* Assignment Logic */}
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: "#64748B",
              letterSpacing: 0.5,
              textTransform: "uppercase",
              mb: 1.5,
            }}
          >
            Assignment Logic
          </Typography>

          <Stack direction="row" spacing={1.5} sx={{ mb: 3 }}>
            <Button
              onClick={() => {
                setAssignmentLogic("specific");
                setSelectedAgent(null);
              }}
              startIcon={<PersonIcon sx={{ fontSize: 18 }} />}
              sx={{
                flex: 1,
                height: 44,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                fontSize: 13,
                border: "1px solid",
                borderColor:
                  assignmentLogic === "specific"
                    ? theme.palette.primary.main
                    : "#E2E8F0",
                bgcolor:
                  assignmentLogic === "specific"
                    ? alpha(theme.palette.primary.main, 0.08)
                    : "#fff",
                color:
                  assignmentLogic === "specific"
                    ? theme.palette.primary.main
                    : "#64748B",
                "&:hover": {
                  bgcolor:
                    assignmentLogic === "specific"
                      ? alpha(theme.palette.primary.main, 0.12)
                      : "#F8FAFC",
                  borderColor:
                    assignmentLogic === "specific"
                      ? theme.palette.primary.main
                      : "#CBD5E1",
                },
              }}
            >
              Specific Agent
            </Button>
            <Button
              onClick={() => {
                setAssignmentLogic("equal");
                setSelectedAgent(null);
              }}
              startIcon={<GridViewIcon sx={{ fontSize: 18 }} />}
              sx={{
                flex: 1,
                height: 44,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                fontSize: 13,
                border: "1px solid",
                borderColor:
                  assignmentLogic === "equal"
                    ? theme.palette.primary.main
                    : "#E2E8F0",
                bgcolor:
                  assignmentLogic === "equal"
                    ? alpha(theme.palette.primary.main, 0.08)
                    : "#fff",
                color:
                  assignmentLogic === "equal"
                    ? theme.palette.primary.main
                    : "#64748B",
                "&:hover": {
                  bgcolor:
                    assignmentLogic === "equal"
                      ? alpha(theme.palette.primary.main, 0.12)
                      : "#F8FAFC",
                  borderColor:
                    assignmentLogic === "equal"
                      ? theme.palette.primary.main
                      : "#CBD5E1",
                },
              }}
            >
              Equal Distribution
            </Button>
          </Stack>

          {/* Team Filter (only for equal distribution) */}
          {assignmentLogic === "equal" && teams.length > 1 && (
            <>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#64748B",
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  mb: 1.5,
                }}
              >
                Filter by Team
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                sx={{ mb: 3, flexWrap: "wrap", useFlexGap: true }}
              >
                {teams.map((team) => (
                  <Chip
                    key={team}
                    label={team === "all" ? "All Teams" : team}
                    onClick={() => setSelectedTeam(team)}
                    icon={
                      team === "all" ? (
                        <GroupIcon sx={{ fontSize: 16 }} />
                      ) : undefined
                    }
                    sx={{
                      height: 32,
                      borderRadius: 2,
                      fontWeight: 600,
                      fontSize: 12,
                      px: 0.5,
                      bgcolor:
                        selectedTeam === team
                          ? theme.palette.primary.main
                          : "#F8FAFC",
                      color: selectedTeam === team ? "#fff" : "#64748B",
                      border: "1px solid",
                      borderColor:
                        selectedTeam === team
                          ? theme.palette.primary.main
                          : "#E2E8F0",
                      "&:hover": {
                        bgcolor:
                          selectedTeam === team
                            ? theme.palette.primary.dark
                            : "#F1F5F9",
                      },
                    }}
                  />
                ))}
              </Stack>
            </>
          )}

          {/* Agent Count for Equal Distribution */}
          {assignmentLogic === "equal" && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                border: "1px solid",
                borderColor: alpha(theme.palette.primary.main, 0.2),
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: theme.palette.primary.main,
                  }}
                >
                  <GroupIcon sx={{ fontSize: 24, color: "#fff" }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}
                  >
                    {filteredTelecallers.length} agent
                    {filteredTelecallers.length !== 1 ? "s" : ""} selected
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "#64748B" }}>
                    Each will get approximately{" "}
                    {filteredTelecallers.length > 0
                      ? Math.ceil(displayCount / filteredTelecallers.length)
                      : 0}{" "}
                    lead
                    {filteredTelecallers.length > 0 &&
                    Math.ceil(displayCount / filteredTelecallers.length) !== 1
                      ? "s"
                      : ""}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}

          {/* Assign to Agent Section */}
          {assignmentLogic === "specific" && (
            <>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#64748B",
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  mb: 1.5,
                }}
              >
                Select Agent
              </Typography>

              {/* Search input */}
              <TextField
                fullWidth
                placeholder="Search agents by name or team..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                sx={{
                  mb: 1.5,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    bgcolor: "#F8FAFC",
                    fontSize: 13,
                    "& fieldset": { borderColor: "#E2E8F0" },
                    "&:hover fieldset": { borderColor: "#CBD5E1" },
                    "&.Mui-focused fieldset": {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#94A3B8", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Agent list */}
              <Box
                sx={{
                  maxHeight: 200,
                  overflowY: "auto",
                  border: "1px solid #E2E8F0",
                  borderRadius: 2,
                  mb: 3,
                  "&::-webkit-scrollbar": { width: 6 },
                  "&::-webkit-scrollbar-track": { bgcolor: "#F8FAFC" },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: "#CBD5E1",
                    borderRadius: 3,
                  },
                }}
              >
                {filteredTelecallers.length > 0 ? (
                  filteredTelecallers.map((agent) => {
                    const isSelected = selectedAgent?.id === agent.id;

                    return (
                      <Box
                        key={agent.id}
                        onClick={() => setSelectedAgent(agent)}
                        sx={{
                          px: 2,
                          py: 1.5,
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          cursor: "pointer",
                          bgcolor: isSelected
                            ? alpha(theme.palette.primary.main, 0.08)
                            : "transparent",
                          borderBottom: "1px solid #F1F5F9",
                          "&:last-child": { borderBottom: "none" },
                          "&:hover": {
                            bgcolor: isSelected
                              ? alpha(theme.palette.primary.main, 0.12)
                              : "#F8FAFC",
                          },
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            fontSize: 13,
                            fontWeight: 600,
                          }}
                        >
                          {getInitials(agent.name)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: "#1E293B",
                              lineHeight: 1.3,
                            }}
                          >
                            {agent.name}
                          </Typography>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                          >
                            <Typography
                              sx={{
                                fontSize: 11,
                                color: "#64748B",
                                lineHeight: 1.3,
                              }}
                            >
                              {agent.team || "No Team"}
                            </Typography>
                            {agent.team && (
                              <Box
                                sx={{
                                  width: 3,
                                  height: 3,
                                  borderRadius: "50%",
                                  bgcolor: "#CBD5E1",
                                }}
                              />
                            )}
                            <Typography
                              sx={{
                                fontSize: 11,
                                color: "#94A3B8",
                                lineHeight: 1.3,
                              }}
                            >
                              {agent.email}
                            </Typography>
                          </Stack>
                        </Box>
                        {isSelected && (
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: "50%",
                              bgcolor: theme.palette.primary.main,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <CheckIcon sx={{ fontSize: 12, color: "#fff" }} />
                          </Box>
                        )}
                      </Box>
                    );
                  })
                ) : (
                  <Box
                    sx={{
                      py: 4,
                      textAlign: "center",
                      color: "#94A3B8",
                    }}
                  >
                    <Typography sx={{ fontSize: 13 }}>
                      No agents found
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}

          {/* Add Internal Note */}
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: "#64748B",
              letterSpacing: 0.5,
              textTransform: "uppercase",
              mb: 1.5,
            }}
          >
            Internal Note (Optional)
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={2}
            placeholder="Add instructions for the assigned agent(s)..."
            value={internalNote}
            onChange={(e) => setInternalNote(e.target.value)}
            sx={{
              mb: 3,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "#F8FAFC",
                fontSize: 13,
                "& fieldset": { borderColor: "#E2E8F0" },
                "&:hover fieldset": { borderColor: "#CBD5E1" },
                "&.Mui-focused fieldset": {
                  borderColor: theme.palette.primary.main,
                },
              },
              "& .MuiOutlinedInput-input": {
                "&::placeholder": { color: "#94A3B8", opacity: 1 },
              },
            }}
          />

          {/* Action Buttons */}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              onClick={handleClose}
              disabled={loading}
              sx={{
                height: 40,
                px: 3,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                color: "#64748B",
                "&:hover": { bgcolor: "#F1F5F9" },
                "&:disabled": { color: "#CBD5E1" },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              variant="contained"
              disabled={isConfirmDisabled}
              startIcon={loading ? <CircularProgress size={16} /> : null}
              sx={{
                height: 40,
                px: 3,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                bgcolor: theme.palette.primary.main,
                boxShadow: "none",
                "&:hover": {
                  bgcolor: theme.palette.primary.dark,
                  boxShadow: "none",
                },
                "&:disabled": {
                  bgcolor: "#CBD5E1",
                  color: "#fff",
                },
              }}
            >
              {loading ? "Assigning..." : "Confirm Assignment"}
            </Button>
          </Stack>
        </Box>
      </Dialog>
    </>
  );
};

export default BulkAssignModal;
